import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { ReactNode, RefObject } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

import { theme } from 'styles/theme'

// Above the dialog's screen glass
const OVERLAY_Z_INDEX = 400
// Minimal height of the overlay, even if the anchor is near the bottom of the window
const MIN_OVERLAY_HEIGHT = 120
// Space kept between the overlay and the bottom of the window
const WINDOW_BOTTOM_MARGIN = 16

const OverlayContainer = styled.div`
  position: fixed;
  z-index: ${OVERLAY_Z_INDEX};
  overflow-y: auto;
  border-radius: ${theme.radii.medium};
  background-color: ${theme.colors.White};
  box-shadow: ${theme.shadows.menu};
`

interface OverlayPosition {
  top: number
  left: number
  width: number
  maxHeight: number
}

interface AnchoredOverlayProps {
  anchorRef: RefObject<HTMLElement>
  // Called on a click outside the overlay and the anchor, or on Escape
  onClose: VoidFunction
  children: ReactNode
}

// A scrollable overlay that opens below its anchor, with the anchor's width.
// It may extend beyond its container (e.g. a dialog), so it is rendered in a portal, outside any container that
// would clip it, and positioned to the anchor's place on the screen.
export const AnchoredOverlay = ({ anchorRef, onClose, children }: AnchoredOverlayProps) => {
  const overlayRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<OverlayPosition | null>(null)

  useLayoutEffect(() => {
    const updatePosition = () => {
      const anchorRect = anchorRef.current?.getBoundingClientRect()
      if (anchorRect === undefined) {
        return
      }
      setPosition({
        top: anchorRect.bottom,
        left: anchorRect.left,
        width: anchorRect.width,
        maxHeight: Math.max(window.innerHeight - anchorRect.bottom - WINDOW_BOTTOM_MARGIN, MIN_OVERLAY_HEIGHT),
      })
    }
    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true) // Capture, to also follow scrolling of the anchor's containers
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [anchorRef])

  useEffect(() => {
    const closeOnOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node
      // Clicks on the anchor are left to the anchor, which usually toggles the overlay
      if (overlayRef.current?.contains(target) || anchorRef.current?.contains(target)) {
        return
      }
      onClose()
    }
    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('mousedown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [anchorRef, onClose])

  if (position === null) {
    return null
  }
  return createPortal(
    <OverlayContainer {...{ ref: overlayRef, style: position }}>{children}</OverlayContainer>,
    document.body,
  )
}
